import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Check, X } from 'lucide-react';
import { CategoryDropdown, type CategorySelection } from '../CategoryDropdown';
import { CategoryIconRenderer } from '@/features/product-library/utils/categoryIcons';
import {
  useMergedCategoriesHierarchy,
  type DomainType,
} from '@/features/product-library/hooks/useMergedCategories';
import { cn } from '@/lib/utils';

interface CategoryEditSectionProps {
  /** Current category value */
  category: string | null;
  /** Current subcategory value */
  subcategory: string | null;
  /** Domain for category filtering */
  productType: 'lighting_fixtures' | 'lv_products' | 'lighting_controls';
  /** Callback when category/subcategory is saved */
  onSave: (category: string, subcategory: string | null) => Promise<void>;
  /** Whether the parent component is in an updating state */
  isUpdating: boolean;
  /** Disable editing */
  disabled?: boolean;
  /** Label to display (default: "Category") */
  label?: string;
  /** Additional class name for the container */
  className?: string;
  /** Allow creating new categories. Default: true for lv_products, false for others */
  allowCategoryCreation?: boolean;
  /** Allow creating new subcategories. Default: true for lv_products/lighting_fixtures, false for controls */
  allowSubcategoryCreation?: boolean;
}

/**
 * CategoryEditSection - A reusable component for editing category/subcategory
 * with view/edit toggle functionality.
 *
 * Features:
 * - View mode: Shows category badge with icon and edit button
 * - Edit mode: Shows CategoryDropdown with save/cancel buttons
 * - Supports inline category/subcategory creation via modal
 * - Auto-updates icon when category changes
 */
export function CategoryEditSection({
  category,
  subcategory,
  productType,
  onSave,
  isUpdating,
  disabled = false,
  label = 'Category',
  className,
  allowCategoryCreation,
  allowSubcategoryCreation,
}: CategoryEditSectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<CategorySelection | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch categories to display icons and names
  const { hierarchy, data: categories = [] } = useMergedCategoriesHierarchy(
    productType as DomainType,
  );

  // Find the current category template for icon display
  const currentTemplate = categories.find(
    c => c.category === category && (subcategory ? c.subcategory === subcategory : !c.subcategory),
  );

  // Find parent template for fallback
  const parentTemplate = categories.find(c => c.category === category && !c.subcategory);

  // Initialize pending selection when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setPendingSelection(category ? { category, subcategory: subcategory || undefined } : null);
    }
  }, [isEditing, category, subcategory]);

  const handleStartEdit = () => {
    if (disabled || isUpdating) return;
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setPendingSelection(null);
  };

  const handleSave = async () => {
    if (!pendingSelection?.category) {
      handleCancel();
      return;
    }

    try {
      setIsSaving(true);
      await onSave(pendingSelection.category, pendingSelection.subcategory || null);
      setIsEditing(false);
      setPendingSelection(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectionChange = (selection: CategorySelection | null) => {
    setPendingSelection(selection);
  };

  // Get display name for the current category
  const getDisplayName = () => {
    if (!category) return 'Not set';

    const template = currentTemplate || parentTemplate;
    if (subcategory) {
      const subTemplate = categories.find(
        c => c.category === category && c.subcategory === subcategory,
      );
      if (subTemplate) {
        return subTemplate.display_name;
      }
      // Fallback: show category > subcategory
      const parentName = template?.display_name || category;
      return `${parentName} > ${subcategory}`;
    }

    return template?.display_name || category;
  };

  // View mode
  if (!isEditing) {
    return (
      <div className={cn('flex justify-between items-center', className)}>
        <Label>{label}</Label>
        <div className="flex items-center gap-2">
          {category ? (
            <div className="flex items-center gap-2">
              <CategoryIconRenderer
                category={category}
                template={currentTemplate || parentTemplate}
                size={18}
                className="text-muted-foreground"
              />
              <Badge variant="secondary" className="capitalize">
                {getDisplayName().replace(/[-_]/g, ' ')}
              </Badge>
            </div>
          ) : (
            <Badge variant="outline" className="text-muted-foreground">
              Not set
            </Badge>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={handleStartEdit}
            disabled={disabled || isUpdating}
            className="h-7 w-7 p-0"
          >
            <Edit className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  // Edit mode
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex justify-between items-center">
        <Label>{label}</Label>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleSave}
            disabled={isSaving || isUpdating || !pendingSelection?.category}
            className="h-7 w-7 p-0"
          >
            <Check className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCancel}
            disabled={isSaving || isUpdating}
            className="h-7 w-7 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <CategoryDropdown
        value={pendingSelection}
        onChange={handleSelectionChange}
        productType={productType}
        disabled={isSaving || isUpdating}
        placeholder={`Select ${label}`}
        layout="vertical"
        displayMode="full"
        allowCategoryCreation={allowCategoryCreation}
        allowSubcategoryCreation={allowSubcategoryCreation}
      />
    </div>
  );
}
